import React, { useEffect, useRef, useState } from "react";
import { Form } from "react-bootstrap";
import UploadIcon from "@mui/icons-material/Upload";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import ImageIcon from "@mui/icons-material/Image";
import AudioFileIcon from "@mui/icons-material/AudioFile";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import VideoFileIcon from "@mui/icons-material/VideoFile";
import { FaFileExcel } from "react-icons/fa";
import SlideshowIcon from "@mui/icons-material/Slideshow"

const UploadFile = ({
  fileType,
  handleFileChange,
  customText,
  variant = "default",
  showFileInfo = false,
  selectedFile = null,
  disabled = false,
  className = "",
  allowMultiple = true,
}) => {
  const fileRef = useRef(null);
  const [accept, setAccept] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const dragCounter = useRef(0); // Add a counter to track drag events

  const choseFile = () => {
    if (!disabled) {
      fileRef?.current.click();
    }
  };

  // File type configuration
  const fileTypeConfig = {
    pdf: {
      accept: "application/pdf",
      icon: <PictureAsPdfIcon className="text-danger" />,
      text: customText || "Upload PDF Document",
      description: "Select or drag PDF files here",
    },
    video: {
      accept: "video/*",
      icon: <VideoFileIcon className="text-primary" />,
      text: customText || "Upload Video",
      description: "Select or drag video files here",
    },
    image: {
      accept: "image/*",
      icon: <ImageIcon className="text-success" />,
      text: customText || "Upload Image",
      description: "Select or drag image files here",
    },
    audio: {
      accept: ".mp3, .wav, .ogg",
      icon: <AudioFileIcon className="text-warning" />,
      text: customText || "Upload Audio",
      description: "Select or drag audio files here",
    },
    xlsx: {
      accept: ".xlsx, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      icon: <FaFileExcel className="text-success" />,
      text: customText || "Upload Excel File",
      description: "Select or drag Excel (.xlsx) files here",
    },
    mixed: {
      accept: "application/pdf, image/*, video/*, .mp3, .wav, .ogg, .xlsx, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      icon: <AttachFileIcon />,
      text: customText || "Upload Files",
      description: "Select or drag multiple types of files here",
    },
    pdfText: {
      accept: "application/pdf, text/plain",
      icon: <AttachFileIcon />,
      text: "Upload PDF or Text",
      description: "Select or drag PDF or text files here",
    },
    ppt: {
      accept: ".pptx, .potx, application/vnd.openxmlformats-officedocument.presentationml.presentation, application/vnd.openxmlformats-officedocument.presentationml.template ",
      icon: <SlideshowIcon />,
      text: "Upload PowerPoint",
      description: "Select or drag PowerPoint (.pptx, .potx) files here",
    },
  };

  useEffect(() => {
    const config = fileTypeConfig[fileType];
    if (config) {
      setAccept(config.accept);
    } else if (typeof fileType === "string") {
      setAccept(fileType); // allows passing custom accept string
    }
  }, [fileType]);

  // ✅ Helper to validate files
  const isValidFile = (file) => {
    if (!accept) return true; // no restriction
    const acceptedTypes = accept.split(",").map((t) => t.trim());
    return acceptedTypes.some((type) => {
      if (type.includes("*")) {
        return file.type.startsWith(type.replace("/*", ""));
      }
      return file.type === type || file.name.toLowerCase().endsWith(type);
    });
  };

  // ✅ Filter and handle selected files
  const processFiles = (files) => {
    const fileArray = Array.from(files);
    const validFiles = fileArray.filter(isValidFile);

    if (validFiles.length !== fileArray.length) {
      alert("Some files were skipped because they are not in the allowed format.");
    }

    if (validFiles.length > 0) {
      handleFileChange({ target: { files: validFiles } });
    }
  };

  // Drag and drop events
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      dragCounter.current += 1; // Increment counter
      if (dragCounter.current === 1) {
        setDragOver(true); // Only set dragOver to true on first enter
      }
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      dragCounter.current -= 1; // Decrement counter
      if (dragCounter.current === 0) {
        setDragOver(false); // Only set dragOver to false when fully left
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = 0; // Reset counter
    setDragOver(false);

    if (!disabled && e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      processFiles(e.dataTransfer.files);
    }
  };

  const currentConfig = fileTypeConfig[fileType] || {
    icon: <AttachFileIcon />,
    text: "Upload File",
    description: "Select or drag files here",
  };

  // Variant styling
  const getVariantClasses = () => {
    switch (variant) {
      case "compact":
        return "p-3";
      case "large":
        return "py-5 px-4";
      default:
        return "py-4 px-3";
    }
  };

  const getIconSize = () => {
    switch (variant) {
      case "compact":
        return { fontSize: "2rem" };
      case "large":
        return { fontSize: "4rem" };
      default:
        return { fontSize: "3rem" };
    }
  };

  return (
    <>
      <Form.Group className="mb-0">
        <Form.Control
          type="file"
          className="d-none"
          accept={accept}
          ref={fileRef}
          onChange={(e) => processFiles(e.target.files)}
          multiple={allowMultiple} // ✅ allow multiple files
          disabled={disabled}
        />
      </Form.Group>

      <div
        className={`
          d-flex flex-column justify-content-center align-items-center
          border border-2 border-dashed rounded-3
          transition-all duration-200
          ${dragOver ? "border-primary bg-primary bg-opacity-10" : "border-secondary"}
          ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
          ${!disabled ? "hover:border-primary hover:bg-light" : ""}
          ${getVariantClasses()}
          ${className}
        `}
        onClick={choseFile}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        style={{
          minHeight: variant === "compact" ? "100px" : variant === "large" ? "200px" : "140px",
          transition: "all 0.2s ease-in-out",
        }}
      >
        {/* Icon */}
        <div
          className={`mb-2 ${dragOver ? "animate-bounce" : ""}`}
          style={getIconSize()}
        >
          {currentConfig.icon}
        </div>

        {/* Main text */}
        <div className={`text-center fw-medium ${variant === "compact" ? "fs-6" : "fs-5"}`}>
          {currentConfig.text}
        </div>

        {/* Description text */}
        {variant !== "compact" && (
          <div className="text-muted text-center small mt-1">
            {currentConfig.description}
          </div>
        )}

        {/* File info */}
        {showFileInfo && selectedFile && (
          <div className="mt-2 text-center">
            <small className="text-success d-block">
              <strong>Selected:</strong> {selectedFile.name}
            </small>
            <small className="text-muted">
              {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
            </small>
          </div>
        )}

        {/* Drag overlay */}
        {dragOver && (
          <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-blue-100 bg-opacity-10 rounded-3">
            <div className="text-white fw-bold">Drop file here</div>
          </div>
        )}
      </div>
    </>
  );
};

export default UploadFile;